import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import React, { useState } from 'react';
import { Wrench, X, Info, Image, Euro, Calendar, MapPin, Car, FileText, Plus, Trash2, Edit } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../../i18n/format.js';
import { toolDocumentAPI, getDocumentUrl } from '../../../api.js';
import ToolDocumentForm from '../forms/ToolDocumentForm.jsx';

function ToolDetailModal({ tool, onClose, onMessage, onUpdate }) {
  const { t, fmt } = useFormat();
  const { money } = useOrgCurrency();
  const [activeTab, setActiveTab] = useState('overview');
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);

  const queryClient = useQueryClient();

  // Fetch documents for this tool
  const { data: documentsData } = useQuery({
    queryKey: ['tool_documents', tool?.id],
    queryFn: () => toolDocumentAPI.getByTool(tool.id),
    enabled: !!tool?.id,
  });

  const documents = documentsData?.data || [];

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: (docId) => toolDocumentAPI.delete(docId),
    onSuccess: () => {
      queryClient.invalidateQueries(['tool_documents', tool.id]);
      onMessage?.(t('tools.messages.documentDeleted'), 'success');
    },
    onError: (error) => {
      onMessage?.(t('tools.messages.deleteError', { error: error.message }), 'error');
    },
  });

  // Create document mutation
  const createDocumentMutation = useMutation({
    mutationFn: (data) => toolDocumentAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tool_documents', tool.id]);
      setShowDocumentForm(false);
      setEditingDocument(null);
      onMessage?.(t('tools.messages.documentAdded'), 'success');
    },
    onError: (error) => {
      onMessage?.(t('tools.messages.addError', { error: error.message }), 'error');
    },
  });

  // Update document mutation
  const updateDocumentMutation = useMutation({
    mutationFn: ({ id, data }) => toolDocumentAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tool_documents', tool.id]);
      setShowDocumentForm(false);
      setEditingDocument(null);
      onMessage?.(t('tools.messages.documentUpdated'), 'success');
    },
    onError: (error) => {
      onMessage?.(t('tools.messages.updateError', { error: error.message }), 'error');
    },
  });

  // Handle document submit
  const handleDocumentSubmit = async (data, documentId) => {
    if (documentId) {
      updateDocumentMutation.mutate({ id: documentId, data });
    } else {
      createDocumentMutation.mutate(data);
    }
  };

  if (!tool) return null;

  // Devise de l'organisation de l'outil - un seul outil monetaire.
  const formatCurrency = (value) => (value ? money(value, tool) : '-');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return fmt.date(dateString);
  };

  const formatCategory = (category) => (
    category ? t(`tools.category.${category}`, { defaultValue: category }) : ''
  );

  const formatStatus = (status) => (
    status ? t(`tools.status.${status}`, { defaultValue: status }) : ''
  );

  const formatCondition = (condition) => (
    condition ? t(`tools.condition.${condition}`, { defaultValue: condition }) : ''
  );

  const getConditionColor = (condition) => {
    switch(condition) {
      case 'new':
      case 'excellent':
        return 'bg-green-100 text-green-800';
      case 'good':
        return 'bg-blue-100 text-blue-800';
      case 'fair':
        return 'bg-yellow-100 text-yellow-800';
      case 'poor':
      case 'broken':
        return 'bg-red-100 text-red-800';
      case 'lost':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'in_maintenance':
        return 'bg-amber-100 text-amber-800';
      case 'out_of_service':
        return 'bg-red-100 text-red-800';
      case 'retired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3">
                <Wrench className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl font-bold">{tool.name}</h2>
                  <p className="text-amber-100">{t('tools.detail.codeLabel', { code: tool.tool_code })}</p>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-50 px-6 py-2 border-b border-gray-200">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Info className="w-4 h-4 inline mr-2" />
              {t('tools.detail.tabs.overview')}
            </button>
            <button
              onClick={() => setActiveTab('photo')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'photo'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Image className="w-4 h-4 inline mr-2" />
              {t('tools.detail.tabs.photo')}
            </button>
            <button
              onClick={() => setActiveTab('financial')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'financial'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Euro className="w-4 h-4 inline mr-2" />
              {t('tools.detail.tabs.financial')}
            </button>
            <button
              onClick={() => setActiveTab('maintenance')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'maintenance'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              {t('tools.detail.tabs.maintenance')}
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'documents'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              {t('tools.detail.tabs.documents')}
              {documents.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                  {documents.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.sections.basic')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.list.columns.code')}</p>
                    <p className="font-medium text-gray-900">{tool.tool_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.list.columns.name')}</p>
                    <p className="font-medium text-gray-900">{tool.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.list.columns.category')}</p>
                    <p className="font-medium text-gray-900">{formatCategory(tool.category)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.detail.fields.type')}</p>
                    <p className="font-medium text-gray-900">{tool.tool_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.list.columns.condition')}</p>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getConditionColor(tool.condition)}`}>
                      {formatCondition(tool.condition)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.list.columns.status')}</p>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(tool.status)}`}>
                      {formatStatus(tool.status)}
                    </span>
                  </div>
                  {tool.description && (
                    <div className="md:col-span-3">
                      <p className="text-sm text-gray-500">{t('tools.form.fields.description')}</p>
                      <p className="font-medium text-gray-900">{tool.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Manufacturer Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.sections.manufacturer')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.manufacturer')}</p>
                    <p className="font-medium text-gray-900">{tool.manufacturer || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.model')}</p>
                    <p className="font-medium text-gray-900">{tool.model || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.version')}</p>
                    <p className="font-medium text-gray-900">{tool.version || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.serialNumber')}</p>
                    <p className="font-medium text-gray-900">{tool.serial_number || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.sections.features')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${tool.is_cordless ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm text-gray-700">{t('tools.form.fields.isCordless')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${tool.has_tracker ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm text-gray-700">{t('tools.form.fields.hasTracker')}</span>
                  </div>
                  {tool.has_tracker && tool.tracker_id && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">{t('tools.detail.fields.trackerId')}</p>
                      <p className="font-medium text-gray-900">{tool.tracker_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.sections.assignment')}</h3>
                <div className="flex items-center gap-3">
                  {tool.assigned_to_type === 'vehicle' && (
                    <>
                      <Car className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-gray-700">{t('tools.assignment.toVehicleId', { id: tool.assigned_vehicle_id })}</span>
                    </>
                  )}
                  {tool.assigned_to_type === 'location' && (
                    <>
                      <MapPin className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-gray-700">{t('tools.assignment.toSiteId', { id: tool.assigned_location_id })}</span>
                    </>
                  )}
                  {tool.assigned_to_type === 'unassigned' && (
                    <span className="text-sm text-gray-500 italic">{t('tools.assignment.unassigned')}</span>
                  )}
                </div>
              </div>

              {/* Notes */}
              {tool.notes && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.fields.notes')}</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{tool.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Photo Tab */}
          {activeTab === 'photo' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.detail.photo.title')}</h3>
                {tool.photo_url ? (
                  <div className="flex justify-center">
                    <img
                      src={tool.photo_url}
                      alt={tool.name}
                      className="max-w-full h-auto max-h-96 rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-center p-12">
                      <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">{t('tools.detail.photo.loadError')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-12">
                    <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('tools.detail.photo.empty')}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {t('tools.detail.photo.emptyHint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === 'financial' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.detail.financial.title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.detail.fields.purchaseDate')}</p>
                    <p className="text-lg font-medium text-gray-900">{formatDate(tool.purchase_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.detail.fields.purchasePrice')}</p>
                    <p className="text-lg font-medium text-gray-900">{formatCurrency(tool.purchase_price)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.warrantyExpiration')}</p>
                    <p className="text-lg font-medium text-gray-900">{formatDate(tool.warranty_expiration_date)}</p>
                  </div>
                </div>

                {tool.warranty_expiration_date && new Date(tool.warranty_expiration_date) < new Date() && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">{t('tools.detail.financial.warrantyExpired')}</p>
                  </div>
                )}

                {tool.warranty_expiration_date && new Date(tool.warranty_expiration_date) >= new Date() && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">{t('tools.detail.financial.warrantyValid')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.detail.maintenance.title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.form.fields.lastMaintenance')}</p>
                    <p className="text-lg font-medium text-gray-900">{formatDate(tool.last_maintenance_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('tools.detail.fields.nextMaintenancePlanned')}</p>
                    <p className="text-lg font-medium text-gray-900">{formatDate(tool.next_maintenance_date)}</p>
                  </div>
                </div>

                {tool.next_maintenance_date && (
                  <div className="mt-6">
                    {new Date(tool.next_maintenance_date) < new Date() ? (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-800">{t('tools.detail.maintenance.overdue')}</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          {t('tools.detail.maintenance.dueIn', {
                            count: Math.ceil((new Date(tool.next_maintenance_date) - new Date()) / (1000 * 60 * 60 * 24))
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!tool.last_maintenance_date && !tool.next_maintenance_date && (
                  <div className="mt-4 text-center p-12">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('tools.detail.maintenance.empty')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{t('tools.detail.documents.title')}</h3>
                  <button
                    onClick={() => {
                      setEditingDocument(null);
                      setShowDocumentForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                  >
                    <Plus className="w-4 h-4" />
                    {t('tools.detail.documents.add')}
                  </button>
                </div>

                {documents.length === 0 ? (
                  <div className="text-center p-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('tools.detail.documents.empty')}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {t('tools.detail.documents.emptyHint')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => {
                      const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                      const daysUntilExpiry = doc.expiry_date
                        ? Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;
                      const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= (doc.alert_days_before || 30);

                      return (
                        <div
                          key={doc.id}
                          className={`p-4 border rounded-lg ${
                            isExpired ? 'border-red-300 bg-red-50' :
                            isExpiringSoon ? 'border-yellow-300 bg-yellow-50' :
                            'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-5 h-5 text-gray-600" />
                                <span className="font-semibold text-gray-900">
                                  {t(`tools.docType.${doc.doc_type}`, { defaultValue: doc.doc_type })}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  doc.status === 'valid' ? 'bg-green-100 text-green-800' :
                                  doc.status === 'expired' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {t(`tools.docStatus.${doc.status}`, { defaultValue: doc.status })}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {doc.doc_number && (
                                  <div>
                                    <span className="text-gray-500">{t('tools.detail.documents.number')} </span>
                                    <span className="text-gray-900">{doc.doc_number}</span>
                                  </div>
                                )}
                                {doc.issuer && (
                                  <div>
                                    <span className="text-gray-500">{t('tools.detail.documents.issuer')} </span>
                                    <span className="text-gray-900">{doc.issuer}</span>
                                  </div>
                                )}
                                {doc.issue_date && (
                                  <div>
                                    <span className="text-gray-500">{t('tools.detail.documents.issueDate')} </span>
                                    <span className="text-gray-900">{formatDate(doc.issue_date)}</span>
                                  </div>
                                )}
                                {doc.expiry_date && (
                                  <div>
                                    <span className="text-gray-500">{t('tools.detail.documents.expiryDate')} </span>
                                    <span className="text-gray-900">{formatDate(doc.expiry_date)}</span>
                                  </div>
                                )}
                              </div>
                              {getDocumentUrl(doc) && (
                                <div className="mt-2">
                                  <a
                                    href={getDocumentUrl(doc)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {t('tools.detail.documents.viewFile')}
                                  </a>
                                </div>
                              )}
                              {doc.notes && (
                                <p className="mt-2 text-sm text-gray-600 italic">{doc.notes}</p>
                              )}
                              {isExpired && (
                                <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded">
                                  <p className="text-sm text-red-800">{t('tools.detail.documents.expired')}</p>
                                </div>
                              )}
                              {isExpiringSoon && (
                                <div className="mt-2 p-2 bg-yellow-100 border border-yellow-200 rounded">
                                  <p className="text-sm text-yellow-800">
                                    {t('tools.detail.documents.expiresIn', { count: daysUntilExpiry })}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => {
                                  setEditingDocument(doc);
                                  setShowDocumentForm(true);
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                title={t('common.actions.edit')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(t('tools.confirm.deleteDocument'))) {
                                    deleteDocumentMutation.mutate(doc.id);
                                  }
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title={t('common.actions.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              {t('common.actions.close')}
            </button>
          </div>
        </div>
      </div>

      {/* Document Form Modal */}
      {showDocumentForm && (
        <ToolDocumentForm
          tool={tool}
          doc={editingDocument}
          onClose={() => {
            setShowDocumentForm(false);
            setEditingDocument(null);
          }}
          onSubmit={handleDocumentSubmit}
          onMessage={onMessage}
        />
      )}
    </div>
  );
}

export default ToolDetailModal;
